import { hasPermission } from "@mnp/shared";
import { useOptionalRole } from "../../lib/role.tsx";
import { useOutboxRows, useRetryDeadOutbox, useRetryOutboxRow } from "../../lib/useIntegrations";

// Карточка outbox-admin-visibility (волна 4). Страница «Интеграции»: оператор
// видит застрявшие/мёртвые исходящие AMO/LOS-вызовы (integration_outbox) и
// перезапускает их кнопкой — без docker-логов. Видна только management
// (право integrations:manage из shared — НЕ дублируем список прав на фронте).

const STATUS_BADGE: Record<string, string> = {
  dead: "red",
  pending: "yellow",
};

const STATUS_LABEL_RU: Record<string, string> = {
  dead: "Мёртвая",
  pending: "Ждёт",
};

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Tbilisi",
  }).format(new Date(iso));
}

export function IntegrationsLanding() {
  const role = useOptionalRole();
  const canManage = hasPermission(role, "integrations:manage");

  const { data, isLoading } = useOutboxRows(canManage);
  const retryRow = useRetryOutboxRow();
  const retryDead = useRetryDeadOutbox();
  const rows = data ?? [];
  const deadCount = rows.filter((r) => r.status === "dead").length;
  // «Застряло» на странице = ТО ЖЕ, что бейдж на главной OpsHub: мёртвые +
  // «залипшие» pending (attempts ≥ 3). Здоровые pending (ждут штатный backoff,
  // attempts < 3) НЕ застряли — они «в очереди». Таблица ниже показывает ВСЕ
  // строки (dead+pending), чтобы оператор видел всю очередь, но сводка не
  // называет здоровые строки застрявшими (иначе бейдж и страница расходятся).
  const stuckCount =
    deadCount + rows.filter((r) => r.status === "pending" && r.attempts >= 3).length;
  const queuedCount = rows.length - stuckCount;

  if (!canManage) {
    return (
      <div className="content">
        <div style={{ padding: 40, textAlign: "center", color: "var(--mnp-fg-3)" }}>
          Недостаточно прав для просмотра интеграций
        </div>
      </div>
    );
  }

  return (
    <div className="content" data-testid="integrations-landing">
      <div className="toolbar">
        <div className="bold" style={{ fontSize: 15 }}>
          Интеграции
        </div>
        <span className="muted" style={{ fontSize: 12 }} data-testid="integrations-summary">
          Застряло: {stuckCount} · в очереди: {queuedCount}
        </span>
        <button
          type="button"
          className="btn"
          data-testid="retry-all-button"
          disabled={deadCount === 0 || retryDead.isPending}
          onClick={() => retryDead.mutate()}
          style={{ marginLeft: "auto" }}
        >
          Повторить все ({deadCount})
        </button>
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Создано</th>
              <th>Заявка</th>
              <th>Куда</th>
              <th>Статус</th>
              <th className="num">Попыток</th>
              <th>Ошибка</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: "center" }}>
                  Загрузка…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ padding: 40, textAlign: "center", color: "var(--mnp-fg-3)" }}
                >
                  Всё чисто — застрявших интеграций нет
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} data-testid={`outbox-row-${row.id}`}>
                <td className="muted">{formatDateTime(row.createdAt)}</td>
                <td className="mono">{row.requestNumber ?? row.aggregateId.slice(0, 8)}</td>
                <td className="mono" style={{ textTransform: "uppercase" }}>
                  {row.target} · {row.kind}
                </td>
                <td>
                  <span className={`badge-a ${STATUS_BADGE[row.status] ?? "gray"}`}>
                    {STATUS_LABEL_RU[row.status] ?? row.status}
                  </span>
                </td>
                <td className="num tnum">{row.attempts}</td>
                <td
                  className="muted"
                  style={{ maxWidth: 320, fontSize: 12 }}
                  title={row.lastError ?? undefined}
                >
                  {row.lastError ?? "—"}
                </td>
                <td className="num">
                  <button
                    type="button"
                    className="btn"
                    data-testid={`retry-button-${row.id}`}
                    disabled={retryRow.isPending}
                    onClick={() => retryRow.mutate(row.id)}
                  >
                    Повторить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
